import axios from 'axios';
import { Business, CreateBusinessData } from '../types/business';

import { API_URL } from '../config/api';
import { shrinkImage } from '../utils/imageCompression';

export interface GoogleReview {
    id: string; rating: number; text: string; language: string; publishTime: string | null;
    ownerReply: string; images: string[];
    author: { name: string; url: string; photo: string };
}
export interface GoogleReviewsResult {
    rating: number | null; count: number | null; googleMapsUri: string;
    collected: number; updatedAt: string | null;
    reviews: GoogleReview[];
    distribution: Record<string, number>;
    history: { month: string; average: number; count: number }[];
}

// Upload progress callback: fraction 0..1 of the request body sent so far.
export type UploadProgressHandler = (fraction: number) => void;

const shrinkImageFields = async (data: any) => {
    const out: any = { ...data };
    if (out.logo instanceof File) out.logo = await shrinkImage(out.logo);
    if (out.mainImage instanceof File) out.mainImage = await shrinkImage(out.mainImage);
    if (Array.isArray(out.gallery)) out.gallery = await Promise.all(out.gallery.map((f: any) => (f instanceof File ? shrinkImage(f) : f)));
    return out;
};


export const businessService = {
    create: async (data: CreateBusinessData, onUploadProgress?: UploadProgressHandler): Promise<Business> => {
        data = await shrinkImageFields(data);
        const formData = new FormData();

        // Append all simple fields
        Object.keys(data).forEach(key => {
            const value = (data as any)[key];
            if (key === 'amenities' || key === 'workingHours' || key === 'categoryIds' || key === 'fingerprint' || key === 'keyPeople' || key === 'additionalInfo' || key === 'menu' || key === 'googleServiceOptions') {
                formData.append(key, JSON.stringify(value));
            }
 else if ((key === 'logo' || key === 'mainImage') && value instanceof File) {
                formData.append(key, value);
            } else if (key === 'mainImage' && typeof value === 'string') {
                formData.append(key, value);
            } else if (key === 'gallery' && Array.isArray(value)) {
                value.forEach((file: File) => {
                    formData.append('gallery', file);
                });
            } else if (key === 'galleryUrls' && Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            } else if (value !== undefined && value !== null && key !== 'logo' && key !== 'mainImage' && key !== 'gallery') {
                formData.append(key, String(value));
            }
        });

        const response = await axios.post(`${API_URL}/businesses`, formData, {
            onUploadProgress: (e) => { if (onUploadProgress && e.total) onUploadProgress(e.loaded / e.total); },
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    update: async (id: string, data: Partial<CreateBusinessData>, onUploadProgress?: UploadProgressHandler): Promise<Business> => {
        data = await shrinkImageFields(data);
        const formData = new FormData();

        // Append all simple fields
        Object.keys(data).forEach(key => {
            const value = (data as any)[key];
            if (key === 'amenities' || key === 'workingHours' || key === 'categoryIds' || key === 'fingerprint' || key === 'keyPeople' || key === 'additionalInfo' || key === 'menu' || key === 'googleServiceOptions') {
                formData.append(key, JSON.stringify(value));
            }
 else if ((key === 'logo' || key === 'mainImage') && value instanceof File) {
                formData.append(key, value);
            } else if (key === 'mainImage' && typeof value === 'string') {
                formData.append(key, value);
            } else if (key === 'gallery' && Array.isArray(value)) {
                value.forEach((file: File) => {
                    formData.append('gallery', file);
                });
            } else if (key === 'galleryUrls' && Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            } else if (value !== undefined && value !== null && key !== 'logo' && key !== 'mainImage' && key !== 'gallery') {
                formData.append(key, String(value));
            }
        });

        const response = await axios.put(`${API_URL}/businesses/${id}`, formData, {
            onUploadProgress: (e) => { if (onUploadProgress && e.total) onUploadProgress(e.loaded / e.total); },
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Potentially add update, delete, getById later
    async getAll(cityId?: string, categoryId?: string, q?: string, limit?: number, excludeIran?: boolean, categorySlug?: string, citySlug?: string) {
        const params: any = {};
        if (cityId) params.cityId = cityId;
        if (categoryId) params.categoryId = categoryId;
        if (q) params.q = q;
        if (limit) params.limit = limit;
        if (excludeIran) params.excludeIran = 'true';
        // Used by the /category/<slug> and /city/<slug> pages.
        if (categorySlug) params.categorySlug = categorySlug;
        if (citySlug) params.citySlug = citySlug;
        const response = await axios.get(`${API_URL}/businesses`, { params });
        return response.data;
    },

    async getById(id: string) {
        const response = await axios.get(`${API_URL}/businesses/${id}`);
        return response.data;
    },

    // The listing's newest Google reviews (collected by the reviews worker) and rating history.
    async getGoogleReviews(id: string): Promise<GoogleReviewsResult> {
        const response = await axios.get(`${API_URL}/businesses/${encodeURIComponent(id)}/google-reviews`);
        return response.data;
    },

    // Admin Methods
    async getAdminBusinesses(userEmail: string, status?: string, q?: string) {
        const response = await axios.get(`${API_URL}/admin/businesses`, {
            params: { userEmail, status, q }
        });
        return response.data;
    },


    async getBusinessStats(userEmail: string) {
        const response = await axios.get(`${API_URL}/admin/business-stats`, {
            params: { userEmail }
        });
        return response.data;
    },

    /** Admin: mark (or unmark) a listing as registered/verified by its owner. */
    // Admin: email the listing's business ("still operating?" check or "listed for free" welcome).
    async sendOutreach(id: string, type: 'verify' | 'welcome') {
        const response = await axios.post(`${API_URL}/admin/businesses/${encodeURIComponent(id)}/outreach`, { type });
        return response.data;
    },
    async cancelOutreach(id: string) {
        const response = await axios.post(`${API_URL}/admin/businesses/${encodeURIComponent(id)}/outreach/cancel`);
        return response.data;
    },
    // Public: the page behind the "still operating?" email link.
    async getListingCheck(token: string) {
        const response = await axios.get(`${API_URL}/listing-check/${encodeURIComponent(token)}`);
        return response.data;
    },
    async answerListingCheck(token: string, answer: 'active' | 'closed') {
        const response = await axios.post(`${API_URL}/listing-check/${encodeURIComponent(token)}`, { answer });
        return response.data;
    },

    async setOwnerVerified(id: string, verified: boolean) {
        const response = await axios.put(`${API_URL}/admin/businesses/${id}/claim`, { verified });
        return response.data;
    },

    async updateStatus(id: string, status: 'approved' | 'rejected' | 'pending' | 'deleted', userEmail: string) {
        const response = await axios.put(`${API_URL}/admin/businesses/${id}/status`, {
            userEmail,
            status
        });
        return response.data;
    },
    async delete(id: string, userEmail: string) {
        const response = await axios.delete(`${API_URL}/businesses/${id}`, {
            params: { userEmail }
        });
        return response.data;
    },
    async likeBusiness(id: string) {
        const response = await axios.post(`${API_URL}/businesses/${id}/like`);
        return response.data;
    },
    async getPerformance(id: string, userEmail: string, startDate?: string, endDate?: string) {
        const response = await axios.get(`${API_URL}/business/${id}/performance`, {
            params: { email: userEmail, startDate, endDate }
        });
        return response.data;
    },
    async getByUser(userEmail: string) {
        const response = await axios.get(`${API_URL}/user/businesses`, {
            params: { email: userEmail }
        });
        return response.data;
    }
};

